import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import { getApplicationsByEventDecrypted } from '../../lib/api/applications';
import { getEventById } from '../../lib/api/events';
import {
  assignGroupMember,
  createItineraryItem,
  deleteItineraryItem,
  getEventGroup,
  listEventGroupMembers,
  listGroupItinerary,
  listGroupMembers,
  removeGroupMember,
  updateEventGroup,
  updateGroupMemberRole,
  updateItineraryItem,
} from '../../lib/api/groups';
import { useToast } from '../../contexts/ToastContext';
import { captureError } from '../../lib/capture-error';
import type {
  ApplicationWithDetails,
  EventGroup,
  EventWithDetails,
  GroupItineraryItem,
  GroupMember,
  GroupMemberRole,
} from '../../types/database';

type ItineraryDraft = {
  id?: string;
  title: string;
  notes: string;
  starts_at: string;
  lat: string;
  lng: string;
  sort_order: string;
};

function toLocalInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function profileLabel(app: ApplicationWithDetails): string {
  const p = app.attendee_profile;
  const name = [p?.first_name, p?.last_name].filter(Boolean).join(' ').trim();
  const email = p?.email || '';
  if (name && email) return `${name} (${email})`;
  return name || email || app.attendee_profile_id.slice(0, 8);
}

const emptyDraft = (sortOrder: number): ItineraryDraft => ({
  title: '',
  notes: '',
  starts_at: '',
  lat: '',
  lng: '',
  sort_order: String(sortOrder),
});

const AdminGroupDetailPage: React.FC = () => {
  const { eventId, groupId } = useParams<{ eventId: string; groupId: string }>();
  const { showToast } = useToast();

  const [event, setEvent] = useState<EventWithDetails | null>(null);
  const [group, setGroup] = useState<EventGroup | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [eventMembers, setEventMembers] = useState<GroupMember[]>([]);
  const [stops, setStops] = useState<GroupItineraryItem[]>([]);
  const [applications, setApplications] = useState<ApplicationWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [groupName, setGroupName] = useState('');
  const [assignProfileId, setAssignProfileId] = useState('');
  const [assignRole, setAssignRole] = useState<GroupMemberRole>('member');
  const [draft, setDraft] = useState<ItineraryDraft>(emptyDraft(1));
  const [busy, setBusy] = useState(false);

  const labelByProfileId = useMemo(() => {
    const map = new Map<string, string>();
    for (const app of applications) {
      map.set(app.attendee_profile_id, profileLabel(app));
    }
    return map;
  }, [applications]);

  const assignedIds = useMemo(
    () => new Set(eventMembers.map((m) => m.attendee_profile_id)),
    [eventMembers]
  );

  const assignable = useMemo(
    () =>
      applications.filter(
        (app) => app.status === 'approved' && !assignedIds.has(app.attendee_profile_id)
      ),
    [applications, assignedIds]
  );

  const loadData = useCallback(async () => {
    if (!eventId || !groupId) return;
    try {
      setLoading(true);
      setError(null);
      const [eventData, groupData, memberData, eventMemberData, stopData, apps] = await Promise.all([
        getEventById(eventId),
        getEventGroup(groupId),
        listGroupMembers(groupId),
        listEventGroupMembers(eventId),
        listGroupItinerary(groupId),
        getApplicationsByEventDecrypted(eventId),
      ]);
      if (!groupData || groupData.event_id !== eventId) {
        setError('Group not found for this event');
        return;
      }
      setEvent(eventData);
      setGroup(groupData);
      setGroupName(groupData.name);
      setMembers(memberData);
      setEventMembers(eventMemberData);
      setStops(stopData);
      setApplications(apps);
      setDraft(emptyDraft(stopData.length + 1));
    } catch (err) {
      captureError(err, 'Error loading group detail');
      setError('Failed to load group');
    } finally {
      setLoading(false);
    }
  }, [eventId, groupId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveGroupName = async () => {
    if (!group || !groupName.trim()) return;
    setBusy(true);
    try {
      const updated = await updateEventGroup(group.id, { name: groupName.trim() });
      setGroup(updated);
      showToast('Group renamed', 'success');
    } catch (err) {
      captureError(err, 'Error renaming group');
      showToast('Failed to rename group', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleAssign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventId || !groupId || !assignProfileId) return;
    setBusy(true);
    try {
      await assignGroupMember({
        eventId,
        groupId,
        attendeeProfileId: assignProfileId,
        role: assignRole,
      });
      const refreshed = await listGroupMembers(groupId);
      setMembers(refreshed);
      setEventMembers(await listEventGroupMembers(eventId));
      setAssignProfileId('');
      setAssignRole('member');
      showToast('Member assigned', 'success');
    } catch (err) {
      captureError(err, 'Error assigning member');
      showToast(err instanceof Error ? err.message : 'Failed to assign member', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRoleChange = async (member: GroupMember, role: GroupMemberRole) => {
    setBusy(true);
    try {
      await updateGroupMemberRole(member.id, member.group_id, role);
      setMembers(await listGroupMembers(member.group_id));
      showToast(role === 'leader' ? 'Leader updated' : 'Role updated', 'success');
    } catch (err) {
      captureError(err, 'Error updating role');
      showToast(err instanceof Error ? err.message : 'Failed to update role', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveMember = async (member: GroupMember) => {
    if (!window.confirm('Remove this member from the group?')) return;
    setBusy(true);
    try {
      await removeGroupMember(member.id);
      setMembers(members.filter((m) => m.id !== member.id));
      if (eventId) {
        setEventMembers(await listEventGroupMembers(eventId));
      }
      showToast('Member removed', 'success');
    } catch (err) {
      captureError(err, 'Error removing member');
      showToast('Failed to remove member', 'error');
    } finally {
      setBusy(false);
    }
  };

  const startEditStop = (item: GroupItineraryItem) => {
    setDraft({
      id: item.id,
      title: item.title,
      notes: item.notes || '',
      starts_at: toLocalInputValue(item.starts_at),
      lat: item.lat != null ? String(item.lat) : '',
      lng: item.lng != null ? String(item.lng) : '',
      sort_order: String(item.sort_order),
    });
  };

  const handleSaveStop = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupId || !draft.title.trim()) return;
    setBusy(true);
    try {
      const payload = {
        title: draft.title.trim(),
        notes: draft.notes || null,
        starts_at: fromLocalInputValue(draft.starts_at),
        lat: draft.lat === '' ? null : Number(draft.lat),
        lng: draft.lng === '' ? null : Number(draft.lng),
        sort_order: Number(draft.sort_order) || 0,
      };
      if (payload.lat != null && Number.isNaN(payload.lat)) {
        showToast('Latitude must be a number', 'error');
        return;
      }
      if (payload.lng != null && Number.isNaN(payload.lng)) {
        showToast('Longitude must be a number', 'error');
        return;
      }

      if (draft.id) {
        await updateItineraryItem(draft.id, payload);
        showToast('Stop updated', 'success');
      } else {
        await createItineraryItem(groupId, payload);
        showToast('Stop added', 'success');
      }
      const next = await listGroupItinerary(groupId);
      setStops(next);
      setDraft(emptyDraft(next.length + 1));
    } catch (err) {
      captureError(err, 'Error saving itinerary stop');
      showToast(err instanceof Error ? err.message : 'Failed to save stop', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteStop = async (item: GroupItineraryItem) => {
    if (!window.confirm(`Delete “${item.title}”?`)) return;
    setBusy(true);
    try {
      await deleteItineraryItem(item.id);
      setStops(stops.filter((s) => s.id !== item.id));
      if (draft.id === item.id) setDraft(emptyDraft(stops.length));
      showToast('Stop deleted', 'success');
    } catch (err) {
      captureError(err, 'Error deleting stop');
      showToast('Failed to delete stop', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="container my-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (error || !event || !group || !eventId || !groupId) {
    return (
      <div className="container my-5">
        <div className="alert alert-danger">{error || 'Group not found'}</div>
        <Link to={eventId ? `/admin/events/${eventId}/groups` : '/admin'} className="btn btn-outline-secondary">
          Back
        </Link>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <Link to={`/admin/events/${eventId}/groups`} className="btn btn-outline-secondary btn-sm mb-3">
        ← All groups
      </Link>
      <h1 className="h3 mb-1">{group.name}</h1>
      <p className="text-muted mb-4">{event.title}</p>

      <div className="card mb-4">
        <div className="card-header">Group settings</div>
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-8">
              <label className="form-label" htmlFor="rename-group">
                Name
              </label>
              <input
                id="rename-group"
                className="form-control"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <button
                type="button"
                className="btn btn-outline-primary w-100"
                disabled={busy || groupName.trim() === group.name}
                onClick={saveGroupName}
              >
                Save name
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">Members ({members.length})</div>
        <div className="card-body border-bottom">
          <form className="row g-2 align-items-end" onSubmit={handleAssign}>
            <div className="col-md-6">
              <label className="form-label" htmlFor="assign-member">
                Approved attendee
              </label>
              <select
                id="assign-member"
                className="form-select"
                value={assignProfileId}
                onChange={(e) => setAssignProfileId(e.target.value)}
                required
              >
                <option value="">Select…</option>
                {assignable.map((app) => (
                  <option key={app.attendee_profile_id} value={app.attendee_profile_id}>
                    {profileLabel(app)}
                  </option>
                ))}
              </select>
              {assignable.length === 0 ? (
                <div className="form-text">
                  No approved attendees left to assign (everyone is already in a group for this event).
                </div>
              ) : null}
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="assign-role">
                Role
              </label>
              <select
                id="assign-role"
                className="form-select"
                value={assignRole}
                onChange={(e) => setAssignRole(e.target.value as GroupMemberRole)}
              >
                <option value="member">Member</option>
                <option value="leader">Leader</option>
              </select>
            </div>
            <div className="col-md-3">
              <button type="submit" className="btn btn-primary w-100" disabled={busy || !assignProfileId}>
                Assign
              </button>
            </div>
          </form>
        </div>
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Attendee</th>
                <th>Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-muted text-center py-3">
                    No members yet.
                  </td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr key={member.id}>
                    <td>
                      {labelByProfileId.get(member.attendee_profile_id) ||
                        member.attendee_profile_id.slice(0, 8)}
                    </td>
                    <td style={{ maxWidth: 140 }}>
                      <Form.Select
                        size="sm"
                        value={member.role}
                        disabled={busy}
                        onChange={(e) =>
                          handleRoleChange(member, e.target.value as GroupMemberRole)
                        }
                      >
                        <option value="member">Member</option>
                        <option value="leader">Leader</option>
                      </Form.Select>
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={busy}
                        onClick={() => handleRemoveMember(member)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">{draft.id ? 'Edit stop' : 'Add itinerary stop'}</div>
        <div className="card-body">
          <form className="row g-3" onSubmit={handleSaveStop}>
            <div className="col-md-6">
              <label className="form-label" htmlFor="stop-title">
                Title
              </label>
              <input
                id="stop-title"
                className="form-control"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                required
              />
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="stop-starts">
                Starts
              </label>
              <input
                id="stop-starts"
                type="datetime-local"
                className="form-control"
                value={draft.starts_at}
                onChange={(e) => setDraft({ ...draft, starts_at: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="stop-order">
                Sort order
              </label>
              <input
                id="stop-order"
                type="number"
                className="form-control"
                value={draft.sort_order}
                onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label" htmlFor="stop-notes">
                Notes / location label
              </label>
              <input
                id="stop-notes"
                className="form-control"
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="stop-lat">
                Latitude
              </label>
              <input
                id="stop-lat"
                className="form-control"
                value={draft.lat}
                onChange={(e) => setDraft({ ...draft, lat: e.target.value })}
                placeholder="37.3382"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label" htmlFor="stop-lng">
                Longitude
              </label>
              <input
                id="stop-lng"
                className="form-control"
                value={draft.lng}
                onChange={(e) => setDraft({ ...draft, lng: e.target.value })}
                placeholder="-121.8863"
              />
            </div>
            <div className="col-12 d-flex gap-2">
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {draft.id ? 'Update stop' : 'Add stop'}
              </button>
              {draft.id ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={busy}
                  onClick={() => setDraft(emptyDraft(stops.length + 1))}
                >
                  Cancel edit
                </button>
              ) : null}
            </div>
          </form>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Itinerary ({stops.length})</div>
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Title</th>
                <th>When</th>
                <th>Notes</th>
                <th>Map</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {stops.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-muted text-center py-3">
                    No stops yet. Add one above.
                  </td>
                </tr>
              ) : (
                stops.map((item) => (
                  <tr key={item.id}>
                    <td>{item.sort_order}</td>
                    <td className="fw-semibold">{item.title}</td>
                    <td className="small">
                      {item.starts_at ? new Date(item.starts_at).toLocaleString() : '—'}
                    </td>
                    <td className="small">{item.notes || '—'}</td>
                    <td className="small">
                      {item.lat != null && item.lng != null
                        ? `${item.lat.toFixed(4)}, ${item.lng.toFixed(4)}`
                        : '—'}
                    </td>
                    <td className="text-end text-nowrap">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary me-2"
                        disabled={busy}
                        onClick={() => startEditStop(item)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={busy}
                        onClick={() => handleDeleteStop(item)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminGroupDetailPage;
